import { NgModule } from '@angular/core';
import { NgxsModule } from '@ngxs/store';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { AppState, appStateDefaults } from './core/store/app/app.state';
import { PatientState, patientDefaults } from './core/store/patient/patient.state';
import { LookupState } from './core/store/lookup/lookup.state';
import { PatientInsurancesState } from './core/store/patient-insurances/patient-insurances.state';
import { NgxsResetPluginModule } from 'ngxs-reset-plugin';
import { OrdersState } from './core/store/order/orders.state';
import { OrderState } from './core/store/order/order.state';
import { PharmOrdersState } from './core/store/pharm-order/pharm-orders.state';
import { PharmAddressState } from './core/store/pharm-address/pharm-address.state';
import { PharmWizardSubmissionState } from './core/store/pharm-wizard-submission/pharm-wizard-submission.state';
import { OrderWizardDataState } from './core/store/order-wizard/order-wizard-data/order-wizard-data.state';
import { OrderWizardNavigationState } from './core/store/order-wizard/order-wizard-navigation/order-wizard-navigation.state';
import { OrderWizardSubmissionState } from './core/store/order-wizard/order-wizard-submission/order-wizard-submission.state';
import { ContactsState } from './core/store/contacts/contacts.state';
import { TestPatientsState, testPatientDefaults } from './core/store/test-patients/test-patients.state';
import { StateClass } from '@ngxs/store/internals';
import { UserState, userDefaults } from './core/store/user/user.state';
import { AddressesState } from './core/store/addresses/addresses.state';
import { MedicalHistoryState } from './core/store/medical-history/medical-history.state';
import { PaymentMethodsState } from './core/store/payment-methods/payment-methods.state';
import { PaymentsState } from './core/store/payments/payments.state';
import { NotificationsState } from './core/store/notifications/notifications.state';
import { pick } from 'lodash-es';
import { AgentState } from './core/store/agent/agent.state';

// TODO find better way to read from EnvService
const isProduction = (window as any).__env.environment === 'production';

// Loading states for all modules, including lazy ones,
// to avoid issue with ngxs @@UPDATE_STATE erasing auth token
const states: StateClass<any>[] = [
  AppState,
  UserState,
  AgentState,
  LookupState,
  PatientState,
  PatientInsurancesState,
  AddressesState,
  PaymentMethodsState,
  PaymentsState,
  OrdersState,
  OrderState,
  PharmOrdersState,
  PharmAddressState,
  PharmWizardSubmissionState,
  ContactsState,
  OrderWizardDataState,
  OrderWizardNavigationState,
  OrderWizardSubmissionState,
  MedicalHistoryState,
  AgentState,
  NotificationsState,
];

if (!isProduction) states.push(TestPatientsState);

@NgModule({
  imports: [
    NgxsModule.forRoot(states, {
      developmentMode: !isProduction,
    }),
    NgxsLoggerPluginModule.forRoot({
      collapsed: false,
      disabled: isProduction,
    }),
    NgxsStoragePluginModule.forRoot({
      key: isProduction ? [AppState, PatientState, UserState] : [AppState, PatientState, UserState, TestPatientsState],
      beforeSerialize: (obj, key) => {
        if (key === 'user') {
          return pick(obj, [
            'isSigningIn',
            'isActivating',
            'authenticatedUser',
            'isAuthenticated',
            'isSessionExpired',
            'isSigningOut',
            'token',
            'version',
          ]);
        } else if (key === 'patient') {
          return pick(obj, ['lastSelectedPatientId', 'preferredLogin', 'preferredCodeMethod', 'version']);
        } else if (key === 'testPatients') {
          return pick(obj, ['testPatient', 'version']);
        }
        return obj;
      },
      afterDeserialize: (obj, key) => {
        if (key === 'user') {
          return { ...userDefaults, ...obj };
        } else if (key === 'patient') {
          return { ...patientDefaults, ...obj };
        } else if (key === 'testPatients') {
          return { ...testPatientDefaults, ...obj };
        }
        return obj;
      },
      migrations: [
        {
          // @ts-ignore
          version: undefined,
          versionKey: 'initialVersion',
          key: 'app',
          migrate: (appState) => {
            if (!appState.version || appState.version < 3) {
              return {
                ...appStateDefaults,
                ...pick(appState, ['isInMaintenance', 'isRefreshByModalRequired', 'lastRefreshed', 'disableCaptcha']),
                version: 3,
              };
            } else {
              return appState;
            }
          },
        },
        {
          // @ts-ignore
          version: undefined,
          versionKey: 'initialVersion',
          key: 'user',
          migrate: (userState) => {
            if (!userState.version || userState.version < 3) {
              return {
                ...userDefaults,
                ...pick(userState, [
                  'authenticatedUser',
                  'isAuthenticated',
                  'isSessionExpired',
                  'isSigningOut',
                  'token',
                ]),
                version: 3,
              };
            } else {
              return userState;
            }
          },
        },
      ],
    }),
    NgxsResetPluginModule.forRoot(),
  ],
})
export class StoreModule {}
